/* iter55f84 — CoachHint primitive.
 *
 * A gentle-mentor tooltip that appears on hover/focus of the wrapped
 * child element. Unlike raw shadcn Tooltip usage across the codebase
 * (single-line "Continue" / "Save" labels), CoachHint is purpose-built
 * for two-part "what to do next" prompts:
 *
 *   • `label`  — 3-5 word action title (bold, oxblood on parchment)
 *   • `body`   — 1-2 sentence mentor voice ("Once you've written 250
 *                words, the wordcount badge on the right unlocks — a
 *                small dopamine hit to keep you going.")
 *
 * Respects the user's existing prompt-mode preference (`guided`,
 * `light`, or `off`) set by the first-login OnboardingPromptModeDialog
 * — coach hints render only when the mode is `guided` or `light`.
 * `off` (or an unset preference) → passthrough with no tooltip.
 *
 * Passing `null`/undefined for `label` and `body` makes the component
 * a passthrough too — so call sites can toggle hints on/off based on
 * state without changing the tree shape.
 *
 * Uses the global TooltipProvider mounted in App.js (200ms delay).
 *
 * ── Coach Actions (iter-coach-actions) ────────────────────────────────────
 * When `onActionsClick` is supplied (chapter-row context), an "⚡ Actions"
 * trigger is appended to the tooltip. Clicking it opens CoachHintDialog
 * with AI-powered editing tools (find insertion point, adjust pacing,
 * adjust character situation, rewrite scene).
 */
import React from 'react';
import { Compass, Zap } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { getPromptMode } from '@/components/OnboardingPromptModeDialog';

export default function CoachHint({
  label,
  body,
  side = 'top',
  align = 'center',
  className = '',
  contentClassName = '',
  disabled = false,
  asChild = true,
  /** Optional — when provided, an "⚡ Actions" button is rendered at the
   *  bottom of the tooltip. Clicking it opens CoachHintDialog. */
  onActionsClick,
  children,
  testId,
}) {
  const mode = getPromptMode();
  const modeAllows = mode === 'guided' || mode === 'light';
  const empty = disabled || !modeAllows || (!label && !body);
  if (empty) return <>{children}</>;

  return (
    <Tooltip>
      <TooltipTrigger asChild={asChild} className={className}>
        {children}
      </TooltipTrigger>
      <TooltipContent
        side={side}
        align={align}
        sideOffset={8}
        data-testid={testId || 'coach-hint'}
        className={
          'z-50 max-w-[300px] rounded-md border p-0 shadow-lg ' +
          'border-[color:var(--gold,#c6a664)]/70 bg-[#fff8ec] text-[#2a1f18] ' +
          contentClassName
        }
      >
        <div className="flex items-start gap-2 border-b border-[color:var(--gold,#c6a664)]/40 px-3 py-1.5">
          <Compass className="mt-[1px] h-3 w-3 shrink-0 text-[#5a1c1f]" aria-hidden />
          <span
            className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[#5a1c1f]"
            style={{ fontFamily: 'Georgia, "Cormorant Garamond", serif' }}
            data-testid={testId ? `${testId}-label` : undefined}
          >
            {label || 'What next?'}
          </span>
        </div>
        {body && (
          <p
            className="max-w-[280px] whitespace-normal px-3 py-2 text-[12px] leading-relaxed"
            style={{ fontFamily: 'Georgia, "Cormorant Garamond", serif' }}
            data-testid={testId ? `${testId}-body` : undefined}
          >
            {body}
          </p>
        )}
        {/* ⚡ Actions trigger — only for chapter-row hints */}
        {onActionsClick && (
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault(); // prevent tooltip from closing before click registers
              e.stopPropagation();
              onActionsClick?.();
            }}
            data-testid={testId ? `${testId}-actions-btn` : 'coach-hint-actions-btn'}
            className="flex w-full items-center gap-1 border-t border-[color:var(--gold,#c6a664)]/40 px-3 py-1.5 text-[11px] font-semibold text-[#5a1c1f] hover:bg-[#5a1c1f]/5 transition-colors"
          >
            <Zap className="h-3 w-3 shrink-0" />
            AI Actions
          </button>
        )}
      </TooltipContent>
    </Tooltip>
  );
}

